import { Box, CircularProgress, Container, Typography } from '@mui/material';
import { Group, Panel, Separator } from 'react-resizable-panels';
import React from 'react';
import TitleIcon from '../../../../../../theme/ui/Atoms/TilteIcon/TitleIcon';
import { ViewLocationIcon } from '../../../../../../assets';
import useTypeEntity from '../useTypeEntity';
import { useTranslation } from 'react-i18next';
import { SubHeaderLocations } from './SubHeaderLocations';
import { SearchBar } from '../../../../../../theme/ui/Atoms/SearchBar/SearchBar';
import { NormalizedGroup } from '../../../../../../redux/structure/GroupsAdapter';
import { useLocation, useNavigate } from 'react-router-dom';
import moment from 'moment';
import { WorkspaceAPIResponse } from '../../../../../../redux/workspace/WorkspaceAPI.model';

interface LocationsLayoutProps {
  children: React.ReactNode;
  innerContainerMaxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  role: string;
  showRightPanel?: boolean;
  rightPanel?: React.ReactNode;
  isHorizontalLayout?: boolean;
}
function LocationsLayout(props: LocationsLayoutProps) {
  const {
    children,
    innerContainerMaxWidth = 'lg',
    role,
    showRightPanel = false,
    rightPanel,
    isHorizontalLayout = false,
  } = props;
  const {
    entity,
    type,
    isLoading,
    groupId,
    pathGroups,
    marketId,
    isLoadingGroups,
  } = useTypeEntity();

  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const isMarkets = location.pathname.includes('markets');
  const onSearch = (item: NormalizedGroup | WorkspaceAPIResponse) => {
    if ((item as NormalizedGroup).uuid) {
      const groupId = (item as NormalizedGroup).uuid;
      if (marketId) {
        return navigate(`/markets/${marketId}/view-locations/${groupId}`);
      }
      return navigate(`/manager/view-locations/${groupId}`);
    }
    const itemMarketId = (item as WorkspaceAPIResponse).id;
    if (itemMarketId) {
      return navigate(`/markets/${itemMarketId}/view-locations`);
    }
  };
  const title = moment().isBefore(moment().startOf('day').add(12, 'hours'))
    ? t('general.good_morning')
    : t('general.good_evening');
  return (
    <Box>
      {(groupId || marketId) && (
        <SubHeaderLocations
          marketId={marketId}
          entity={entity}
          type={type}
          loading={isLoading}
          pathGroups={pathGroups}
        />
      )}
      <Container maxWidth="xl">
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
            my: 4,
          }}
        >
          <Box flex={'2 1 auto'}>
            <Box>
              <Typography
                variant="h2"
                fontWeight={'bold'}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                }}
                mt={4}
                mb={0}
                color="primary"
              >
                {isMarkets ? title : null}
              </Typography>
            </Box>
          </Box>
          <Box flex={'1 1 auto'}>
            <SearchBar selectItem={onSearch}>
              <SearchBar.MarketsSearch />
              <SearchBar.BUSearch />
              <SearchBar.RegionSearch />
              <SearchBar.StoreSearch />
            </SearchBar>
          </Box>
        </Box>
        <TitleIcon
          icon={
            isLoadingGroups ? (
              <CircularProgress size={'2em'} />
            ) : (
              <ViewLocationIcon height={'2em'} />
            )
          }
        >
          {!isLoadingGroups &&
            `${t('view_locations.view_all')} ${t(`view_locations.${role}`)}`}
        </TitleIcon>
        <Box
          sx={(theme) => ({
            mt: 4,
            overflow: 'hidden',
            width: '100%',
            '& .separator': {
              backgroundColor: theme.palette.background.paper,
              transition: 'background 0.2s',
              width: 8,
              cursor: 'col-resize',
            },
            '& .separator[data-separator="hover"]': {
              backgroundColor: theme.palette.primary.light,
            },
            '& .separator[data-separator="active"]': {
              backgroundColor: theme.palette.primary.main,
            },
          })}
        >
          {showRightPanel ? (
            isHorizontalLayout ? (
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  mt: 4,
                  overflow: 'hidden',
                  flexDirection: 'column',
                }}
              >
                <Box
                  className="transition-p"
                  sx={{
                    transition: 'flex-basis 0.3s ease-in-out 0.225s',
                    overflow: 'auto',
                    maxHeight: '600px',
                    flex: '0 0 50%',
                  }}
                >
                  <Container
                    maxWidth={innerContainerMaxWidth}
                    sx={{
                      overflow: 'auto',
                    }}
                  >
                    {children}
                  </Container>
                </Box>

                <Box
                  sx={{
                    flex: '0 0 50%',
                    overflow: 'auto',
                    width: '100%',
                  }}
                >
                  {rightPanel}
                </Box>
              </Box>
            ) : (
              <Group
                orientation={'horizontal'}
                style={{
                  width: '100%',
                  height: 'auto',
                  display: 'flex',
                }}
              >
                <Panel
                  defaultSize="50%"
                  minSize={10}
                  style={{ overflow: 'auto' }}
                >
                  <Container
                    maxWidth={innerContainerMaxWidth}
                    sx={{ overflow: 'auto' }}
                  >
                    {children}
                  </Container>
                </Panel>
                <Separator className="separator" />
                <Panel
                  defaultSize="50%"
                  minSize={10}
                  style={{ overflow: 'auto' }}
                >
                  {rightPanel}
                </Panel>
              </Group>
            )
          ) : (
            <Container
              maxWidth={innerContainerMaxWidth}
              sx={{ overflow: 'auto' }}
            >
              {children}
            </Container>
          )}
        </Box>
      </Container>
    </Box>
  );
}

export default LocationsLayout;
